import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney, toNumber } from '../ui/format'
import { JournalAmend, type AmendableLine } from '../ui/JournalAmend'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

type PartyKind = 'EMPLOYEE' | 'SUPPLIER'

/**
 * بابٌ من أبواب الصندوق — <b>وهو حسابٌ في الدفتر، لا كلمةٌ في عمود</b>.
 *
 * ⚠ <b>وهذه الأبوابُ مأخوذةٌ من دفتر المالك في الإكسل نفسِه</b>: رواتب ·
 *   إيجار · مصاريفُ المحلّ · مشترياتُ بضاعة · أقساطُ الزبائن. فالشاشةُ تكتب ما
 *   كان يكتبه، بلا أن يتعلّم شيئًا جديدًا.
 *
 * ⚠ <b>والموظّفُ والمورّدُ يُختاران بأسمائهم لا يُكتبان</b> (<c>0048</c>): «مجد»
 *   و«مجد فروخ» في الإكسل كانا يختلطان، فلا يُعرف كم دُفع لكلٍّ منهما. والاسمُ
 *   المختارُ يحمله سطرُ القيد، فيُجمع رصيدُ كلٍّ منهما من الدفتر.
 */
type Door = {
  id: string
  label: string
  /** الحسابُ المقابل للصندوق — أو المدينُ في «مستحقّ». */
  account: string
  /**
   * «خارج» يُنقص الصندوق، و«داخل» يزيده، و«مستحقّ» لا يمسّ مالًا: يُثبت أنّ
   * للموظّف علينا راتبًا لم يُدفع بعد.
   */
  way: 'out' | 'in' | 'accrue'
  party?: PartyKind
  /** في «مستحقّ»: الحسابُ الدائن. */
  against?: string
  /** بابٌ يدخل الصندوقَ وحدَه — لا البنك. */
  cashOnly?: boolean
  hint: string
}

const DOORS: readonly Door[] = [
  { id: 'employee', label: 'دفعةٌ لموظّف',         account: '2300', way: 'out', party: 'EMPLOYEE', hint: 'يوميّة · راتب · سلفة · عمولة — تُنقص ما له علينا' },
  { id: 'supplier', label: 'دفعةٌ لمورّد',          account: '2100', way: 'out', party: 'SUPPLIER', hint: 'تُنقص ما له علينا' },
  { id: 'rent',     label: 'إيجار',                 account: '5400', way: 'out', hint: '' },
  { id: 'utility',  label: 'كهرباءٌ وماء',          account: '5500', way: 'out', hint: '' },
  { id: 'internet', label: 'إنترنتٌ وهاتف',          account: '5510', way: 'out', hint: '' },
  { id: 'shop',     label: 'مصاريفُ المحلّ والقرطاسيّة', account: '5600', way: 'out', hint: 'أكياس · توصيل · قرطاسيّة · صيانة' },
  { id: 'jamiya',   label: 'قسطُ الجمعيّة',          account: '1300', way: 'out', hint: 'مالُك محبوسٌ حتّى دورك — لا مصروف' },
  { id: 'other',    label: 'مصاريفُ أخرى',          account: '5900', way: 'out', hint: '' },
  { id: 'drawing',  label: 'مسحوباتٌ شخصيّة',      account: '3200', way: 'out', hint: 'ما يأخذه المالكُ لنفسه' },
  { id: 'install',  label: 'قسطٌ من زبون',          account: '1100', way: 'in',  hint: 'مصطفى · عبدالله · يزن …' },
  { id: 'capital',  label: 'إيداعٌ من المالك',      account: '3100', way: 'in',  hint: 'مالٌ وضعه المالكُ في المحلّ' },
  { id: 'card',     label: 'فلوسُ البطاقة من صاحب الماكنة', account: '1020', way: 'in', cashOnly: true, hint: 'ما بيع بالبطاقة وسلّمك إيّاه نقدًا' },
  { id: 'accrue',   label: 'راتبٌ مستحقٌّ لموظّف',  account: '5300', way: 'accrue', against: '2300', party: 'EMPLOYEE', hint: 'راتبٌ صار له ولم يُدفع بعد — لا يمسّ الصندوق' },
]

/** الصندوقُ نقدًا والبنكُ تحويلًا — من أين خرج المالُ أو إلى أين دخل. */
const PURSES = [
  { account: '1010', label: 'الصندوق (نقدًا)' },
  { account: '1020', label: 'البنك / البطاقة' },
] as const

type Payee = { id: string; name: string }

/**
 * قيدٌ كما يردّه `JournalView` في الخادم.
 *
 * ⚠ **والأسماءُ من العقد لا من التخمين** — خُمّنت أوّلًا `entryNo` و
 *   `totalDebit`، فكانت القائمةُ ستعرض مبالغَ صفرًا لكلّ حركة.
 */
type Journal = {
  id: string
  journalNo: string
  entryDate: string
  memo: string | null
  sourceType: string
  total: number
  lines: AmendableLine[]
}

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())
}

/**
 * الصندوق — <b>دفترُ الإكسل نفسُه، لكنّه يُقرأ ويُجمَع</b>.
 *
 * <p>
 * ⚠ <b>وكلُّ حركةٍ قيدٌ متوازنٌ في الدفتر</b> — لا سطرٌ في جدولٍ جانبيّ.
 * فالمصروفُ يظهر في الأرباح والخسائر، والدفعةُ للمورّد تُنقص ذمّتَه، والقسطُ
 * يُنقص ما على الزبون. <b>بلا أن يكتب أحدٌ مدينًا ودائنًا.</b>
 * </p>
 *
 * <p>
 * ⚠ <b>وما يخرج من الدرج أثناء الورديّة يُكتب في نقطة البيع لا هنا</b>: هناك
 * ينقص متوقَّعُ الورديّة معه. وهذه الشاشةُ لما يخرج من خارج الدرج — شيكُ
 * الإيجار، والتحويل، وما دفعه المالكُ من جيبه.
 * </p>
 */
export default function CashBook() {
  const { can } = useAuth()
  const queries = useQueryClient()

  const [doorId, setDoorId] = useState(DOORS[0].id)
  const [purse, setPurse] = useState<string>(PURSES[0].account)
  const [amount, setAmount] = useState('')
  const [partyId, setPartyId] = useState('')
  const [who, setWho] = useState('')
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [date, setDate] = useState(today())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const door = DOORS.find((d) => d.id === doorId) ?? DOORS[0]
  const value = toNumber(amount) ?? 0
  const from = door.cashOnly ? '1010' : purse

  // والحركاتُ اليدويّةُ وحدَها — لا قيودُ البيع والاستلام التي تكتبها الشاشاتُ الأخرى،
  // ولا ما عُكس منها: أثرُه صفر.
  const recent = useQuery({
    queryKey: ['cashbook'],
    queryFn: () => api<Journal[]>('/api/v1/accounting/journals?sourceType=MANUAL&limit=30&hideReversed=true'),
    enabled: can(Perm.ledgerView),
  })

  // الأسماءُ من القائمة نفسِها التي يراها الكاشير — موظّفون ومورّدون.
  const payees = useQuery({
    queryKey: ['payout-options'],
    queryFn: () => api<{ employees: Payee[]; suppliers: Payee[] }>('/api/v1/cash-payouts/options'),
    enabled: door.party !== undefined,
    staleTime: 60_000,
  })

  const choices = door.party === 'EMPLOYEE'
    ? payees.data?.employees ?? []
    : door.party === 'SUPPLIER' ? payees.data?.suppliers ?? [] : []

  const chosen = choices.find((p) => p.id === partyId)
  const ready = value > 0 && (door.party === undefined || chosen !== undefined)

  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(null)

    try {
      const name = chosen?.name ?? who.trim()
      const memo = [door.label, name, note.trim()].filter(Boolean).join(' — ')
      const party = door.party && chosen ? { partyKind: door.party, partyId: chosen.id } : {}

      // ⚠ **والاتّجاهُ يحدّد الساقين، ولا يُسأل عنه أحد**: «خارج» يُنقص
      //   الصندوقَ (دائن) ويُثبت البابَ (مدين)، و«داخل» عكسُه، و«مستحقّ»
      //   مصروفٌ مدينٌ والتزامٌ دائنٌ باسم الموظّف.
      const lines = door.way === 'out'
        ? [
            { accountCode: door.account, debit: value, credit: 0, memo: name || null, ...party },
            { accountCode: from, debit: 0, credit: value, memo: null },
          ]
        : door.way === 'in'
          ? [
              { accountCode: from, debit: value, credit: 0, memo: null },
              { accountCode: door.account, debit: 0, credit: value, memo: name || null },
            ]
          : [
              { accountCode: door.account, debit: value, credit: 0, memo: name || null, ...party },
              { accountCode: door.against!, debit: 0, credit: value, memo: name || null, ...party },
            ]

      await api('/api/v1/accounting/journals', {
        method: 'POST',
        body: { memo, lines, entryDate: date },
      })

      setSaved(`${door.way === 'out' ? 'خرج' : door.way === 'in' ? 'دخل' : 'استُحقّ'} ${formatMoney(value)} — ${memo}`)
      setAmount('')
      setWho('')
      setNote('')
      setPartyId('')

      await Promise.all([
        queries.invalidateQueries({ queryKey: ['cashbook'] }),
        queries.invalidateQueries({ queryKey: ['balances'] }),
      ])
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  if (!can(Perm.journalPost)) {
    return (
      <Panel title="الصندوق">
        <Banner tone="warn">
          هذه الشاشةُ تكتب المصاريفَ والدفعاتِ في الدفتر، وتحتاج صلاحيّةَ «كتابةُ قيد».
        </Banner>
      </Panel>
    )
  }

  return (
    <Panel title="الصندوق — مصاريفُ ودفعات">
      <Banner tone="info">
        ما يخرج من <b>درج الورديّة</b> يُكتب من نقطة البيع («مصروفٌ من الدرج») كي ينقص متوقَّعُ الورديّة معه.
        وهذه الشاشةُ لما يخرج من خارج الدرج.
      </Banner>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="الباب" hint={door.hint || undefined}>
          <Select value={doorId} onChange={(id) => { setDoorId(id); setPartyId('') }}>
            <optgroup label="خارجٌ من المحلّ">
              {DOORS.filter((d) => d.way === 'out').map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </optgroup>
            <optgroup label="داخلٌ إلى المحلّ">
              {DOORS.filter((d) => d.way === 'in').map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </optgroup>
            <optgroup label="بلا مال — مستحقّ">
              {DOORS.filter((d) => d.way === 'accrue').map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </optgroup>
          </Select>
        </Field>

        {door.way !== 'accrue' && !door.cashOnly && (
          <Field label={door.way === 'out' ? 'خرج من' : 'دخل إلى'}>
            <Select value={purse} onChange={setPurse}>
              {PURSES.map((p) => (
                <option key={p.account} value={p.account}>{p.label}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="المبلغ">
          <NumberInput value={amount} onChange={setAmount} placeholder="0.000" />
        </Field>

        <Field label="التاريخ">
          <TextInput value={date} onChange={setDate} placeholder="2026-09-28" />
        </Field>

        {door.party ? (
          <Field label={door.party === 'EMPLOYEE' ? 'الموظّف' : 'المورّد'}>
            {payees.isPending ? (
              <Spinner />
            ) : (
              <Select value={partyId} onChange={setPartyId}>
                <option value="">— اختر —</option>
                {choices.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field label="لمَن / مِن مَن" hint="الاسم — ويُبحث به لاحقًا">
            <TextInput value={who} onChange={setWho} placeholder="ماهر، مصطفى…" />
          </Field>
        )}

        <Field label="ملاحظة">
          <TextInput value={note} onChange={setNote} placeholder="اختياريّ" />
        </Field>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span
          className={`tabular text-lg font-bold ${door.way === 'in' ? 'text-ok' : 'text-warn'}`}
          dir="ltr"
        >
          {door.way === 'in' ? '+' : door.way === 'out' ? '−' : ''}{formatMoney(value)}
        </span>

        <Button tone="primary" disabled={busy || !ready} onClick={() => void save()}>
          {busy ? 'يُحفظ…' : 'احفظ'}
        </Button>
      </div>

      {saved && <div className="mt-3"><Banner tone="ok">{saved}</Banner></div>}
      <ErrorBanner error={error ?? recent.error ?? payees.error} />

      <h3 className="mt-6 mb-2 text-sm font-semibold text-ink-soft">آخرُ الحركات</h3>

      {recent.isPending ? (
        <Spinner label="يُقرأ الدفتر…" />
      ) : (recent.data ?? []).length === 0 ? (
        <Empty>لا حركاتٍ بعد.</Empty>
      ) : (
        <ul className="grid gap-1">
          {(recent.data ?? []).map((j) => (
            <li key={j.id} className="rounded-lg border border-line px-3 py-2 text-sm">
              <div className="flex items-baseline gap-3">
                <span className="tabular shrink-0 text-xs text-ink-soft" dir="ltr">{j.entryDate}</span>
                <span className="min-w-0 flex-1 truncate">{j.memo ?? j.journalNo}</span>
                <span className="tabular shrink-0 font-bold" dir="ltr">{formatMoney(j.total)}</span>
                {can(Perm.journalReverse) && editing !== j.id && (
                  <Button onClick={() => setEditing(j.id)}>عدّل</Button>
                )}
              </div>
              {editing === j.id && (
                <JournalAmend journal={j} onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}
